import { Reveal } from "./Reveal";
import { HeartHandshake, Baby, PiggyBank, Check } from "lucide-react";

const SECTIONS = [
  {
    id: "separazione",
    icon: HeartHandshake,
    overline: "Separazione e divorzio",
    title: "Separarsi senza trasformarlo in una guerra",
    img: "/assets/stretta.jpg",
    imgAlt: "Illustrazione: due persone che si stringono la mano",
    imgClass: "object-cover object-left",
    points: [
      "Evitare anni di contenzioso in tribunale e il logorio emotivo che ne deriva",
      "Ridurre drasticamente tempi e spese legali della separazione",
      "Raggiungere accordi che i legali potranno recepire e far omologare",
    ],
  },
  {
    id: "figli",
    icon: Baby,
    overline: "La centralità dei figli",
    title: "I figli al centro, non in mezzo al conflitto",
    img: "/assets/ponte.jpg",
    imgAlt: "Illustrazione Oltre il Conflitto: la famiglia sul ponte",
    points: [
      "Costruire una bigenitorialità effettiva: entrambi i genitori restano punto di riferimento",
      "Pianificare insieme tempi, vacanze, scuola, salute e quotidianità dei figli",
      "Proteggere i minori dall'esposizione al conflitto tra gli adulti",
    ],
  },
];

const STATS = [
  { value: "8–12", label: "incontri, in media, per un percorso completo" },
  { value: "3–5", label: "mesi invece degli anni di una causa giudiziale" },
  { value: "-80%", label: "di costi indicativi rispetto a un contenzioso in tribunale" },
];

export const DeepDiveSections = () => (
  <section id="percorsi" data-testid="deep-dive-sections" className="py-20 lg:py-32 bg-sand border-y border-ink/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-3xl mb-14 lg:mb-20">
        <p className="overline-tag mb-4">I tre ambiti</p>
        <h2 className="font-serif font-semibold text-ink tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
          Separazione, figli, costi: le domande di sempre, risposte chiare.
        </h2>
      </Reveal>

      <div className="flex flex-col gap-16 lg:gap-24">
        {SECTIONS.map((s, i) => (
          <div key={s.id} id={s.id} data-testid={`deep-dive-${s.id}`}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "" : ""}`}>
            <Reveal className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl border border-gold/50 pointer-events-none" />
                <img src={s.img} alt={s.imgAlt} loading="lazy"
                  className={`relative rounded-2xl w-full h-72 lg:h-96 shadow-lg bg-white ${s.imgClass || "object-cover"}`} />
              </div>
            </Reveal>
            <Reveal delay={0.15} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <span className="inline-flex items-center gap-2 rounded-full bg-goldlight text-golddeep font-display text-xs font-semibold uppercase tracking-[0.18em] px-4 py-1.5">
                <s.icon size={14} /> {s.overline}
              </span>
              <h3 className="mt-5 font-serif font-semibold text-ink text-xl sm:text-2xl lg:text-3xl leading-snug">{s.title}</h3>
              <ul className="mt-6 flex flex-col gap-4">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-mist leading-relaxed">
                    <span className="mt-1 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink text-paper">
                      <Check size={12} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        ))}

        <div id="costi" data-testid="deep-dive-costi">
          <Reveal>
            <div className="bg-night rounded-3xl px-8 py-12 lg:px-16 lg:py-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
              <div className="flex items-center gap-3 mb-4">
                <PiggyBank size={20} className="text-gold" />
                <p className="font-display text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold text-gold">Tempi e costi trasparenti</p>
              </div>
              <h3 className="font-serif font-semibold text-paper text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl">
                Costi concordati prima di iniziare. Nessuna sorpresa, mai.
              </h3>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
                {STATS.map((s) => (
                  <div key={s.label} className="border-t border-gold/30 pt-5">
                    <span className="font-serif text-4xl lg:text-5xl font-semibold text-gold">{s.value}</span>
                    <p className="mt-2 text-sm text-paper/70 leading-relaxed">{s.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-10 text-xs sm:text-sm text-paper/50 max-w-2xl leading-relaxed">
                Il primo colloquio è informativo e senza impegno: serve a capire se la mediazione è adatta
                alla tua situazione. La tariffa a incontro viene sempre comunicata in anticipo, per iscritto.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
